import {
  parseCvssVectorMetricValues,
  stripEnvironmentalMetricsFromVector,
  type FindingCvssVersionKey,
} from '@terra/shared-cvss';

export type CvssMetricGroupKey =
  | 'exploitability'
  | 'scope'
  | 'impact'
  | 'subsequentImpact';

export type CvssMetricValueFamily =
  | 'attackVector'
  | 'attackComplexity'
  | 'attackRequirements'
  | 'privilegesRequired'
  | 'userInteraction'
  | 'scope'
  | 'impact';

export type CvssMetricTone = 'raises' | 'some' | 'lowers';

export type CvssVectorMetric = {
  metric: string;
  value: string | null;
  family: CvssMetricValueFamily;
  tone: CvssMetricTone | null;
  reason: string | null;
};

export type CvssVectorGroup = {
  group: CvssMetricGroupKey;
  metrics: CvssVectorMetric[];
};

type MetricLayout = {
  group: CvssMetricGroupKey;
  metrics: readonly { metric: string; family: CvssMetricValueFamily }[];
};

const CVSS_40_LAYOUT: readonly MetricLayout[] = [
  {
    group: 'exploitability',
    metrics: [
      { metric: 'AV', family: 'attackVector' },
      { metric: 'AC', family: 'attackComplexity' },
      { metric: 'AT', family: 'attackRequirements' },
      { metric: 'PR', family: 'privilegesRequired' },
      { metric: 'UI', family: 'userInteraction' },
    ],
  },
  {
    group: 'impact',
    metrics: [
      { metric: 'VC', family: 'impact' },
      { metric: 'VI', family: 'impact' },
      { metric: 'VA', family: 'impact' },
    ],
  },
  {
    group: 'subsequentImpact',
    metrics: [
      { metric: 'SC', family: 'impact' },
      { metric: 'SI', family: 'impact' },
      { metric: 'SA', family: 'impact' },
    ],
  },
];

const CVSS_31_LAYOUT: readonly MetricLayout[] = [
  {
    group: 'exploitability',
    metrics: [
      { metric: 'AV', family: 'attackVector' },
      { metric: 'AC', family: 'attackComplexity' },
      { metric: 'PR', family: 'privilegesRequired' },
      { metric: 'UI', family: 'userInteraction' },
    ],
  },
  { group: 'scope', metrics: [{ metric: 'S', family: 'scope' }] },
  {
    group: 'impact',
    metrics: [
      { metric: 'C', family: 'impact' },
      { metric: 'I', family: 'impact' },
      { metric: 'A', family: 'impact' },
    ],
  },
];

const LAYOUTS: Record<FindingCvssVersionKey, readonly MetricLayout[]> = {
  '4.0': CVSS_40_LAYOUT,
  '3.1': CVSS_31_LAYOUT,
};

const VALUE_TONES: Record<
  CvssMetricValueFamily,
  Partial<Record<string, CvssMetricTone>>
> = {
  attackVector: { N: 'raises', A: 'some', L: 'lowers', P: 'lowers' },
  attackComplexity: { L: 'raises', H: 'lowers' },
  attackRequirements: { N: 'raises', P: 'lowers' },
  privilegesRequired: { N: 'raises', L: 'some', H: 'lowers' },
  userInteraction: { N: 'raises', P: 'some', R: 'some', A: 'lowers' },
  scope: { C: 'raises', U: 'lowers' },
  impact: { H: 'raises', L: 'some', N: 'lowers' },
};

export const buildCvssVectorGroups = ({
  version,
  vector,
  metricReasons,
}: {
  version: FindingCvssVersionKey;
  vector: string | null;
  metricReasons: readonly { metric: string; reason: string }[];
}): CvssVectorGroup[] => {
  const values = parseCvssVectorMetricValues(
    stripEnvironmentalMetricsFromVector(vector ?? ''),
  );

  return LAYOUTS[version].map(({ group, metrics }) => ({
    group,
    metrics: metrics.map(({ metric, family }) => {
      const value = values.get(metric) ?? null;
      const reason = metricReasons.find((row) => row.metric === metric);

      return {
        metric,
        value,
        family,
        tone: value ? (VALUE_TONES[family][value] ?? null) : null,
        reason: reason?.reason.trim() || null,
      };
    }),
  }));
};
